<script setup>
import { ref, onMounted, watch, nextTick } from 'vue'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'

// Import your useSearch composable
const { 
  searchQuery,
  searchResults,
  getSearchResults,
  listFocusIndex,
  loadMoreResults,
  filteringSpecialty
} = useSearch()

// Map instance & markers
let map = null
const doctor_markers = []

// Mobile toggle: default hidden on mobile
const showMapOnMobile = ref(false)

onMounted(() => {
  init_map()

  // Watch for new search results, re-populate markers
  watch(searchResults, (newValue) => {
    if (!newValue || !newValue.length) return
    doctor_markers.forEach(marker => marker.remove())
    doctor_markers.length = 0
    populate_markers(searchResults)
  })

  // Watch for listFocusIndex changes (focusing on an item)
  watch(listFocusIndex, (newVal, oldVal) => {
    if (newVal !== oldVal) {
      console.debug(`Map: List Focus Changed from ${oldVal} to ${newVal}`)
      doctor_markers.forEach(marker => marker.remove())
      doctor_markers.length = 0
      populate_markers(searchResults, newVal)
    }
  })

  // If the map is hidden on mobile, we need to call map.resize() when it becomes visible
  watch(showMapOnMobile, async (visible) => {
    if (visible && map) {
      await nextTick()
      map.resize()
    }
  })
})

function init_map() {
  if (map) return
  if (process.client){
    mapboxgl.accessToken = 'pk.eyJ1Ijoid29sZm1vbiIsImEiOiJjbGkzOHhmaG4yMW9yM3RwOWxmamJoODA0In0.bSQjC86ONCZVVv184YjFeA'
    map = new mapboxgl.Map({
      container: 'doctor-map',
      style: 'mapbox://styles/wolfmon/ckapb9g0z1tv31iqpmzbars4o',
      center: [-73.80375, 40.686658],
      zoom: 10.5
    })
  }
}

function populate_markers(searchResults, focusIndex = null) {
  for (const i in searchResults.value) {
    const location = searchResults.value[i]
    location.cn_specialty_1 = location.cn_specialty_1 ?? ''

    const coordinate = JSON.parse(location.geojson).coordinates

    // Example star icon
    const star = `
      <svg xmlns="http://www.w3.org/2000/svg" fill="yellow" viewBox="0 0 24 24" stroke-width="1.5" stroke="none" class="w-6 h-6 inline">
        <path stroke-linecap="round" stroke-linejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.563 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
      </svg>`

    const stars = location.tier ? star.repeat(location.tier) : ''

    const popup_html = `
      <div class="text-lg font-medium text-primaryText mx-2 mb-1">
        ${location.cn_name} ${location.cn_specialty_1}医师
        ${stars}
      </div>
      <div class="text-sm text-primaryText/90 mx-2">
        ${location.en_full_address}
      </div>
      <a href="/doctor/${location.cn_specialty_1}/${location.cn_name}医生">
        <button 
          class="mx-2 mt-5 inline-flex items-center rounded-md bg-primaryBtn 
                 px-2 py-1 text-sm font-semibold text-white shadow-sm
                 hover:bg-primaryBtnHover hover:text-white"
        >
          详细信息
        </button>
      </a>
    `

    const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(popup_html)

    if (focusIndex != null && focusIndex == i) {
      // Fly to this coordinate, highlight marker
      map.flyTo({
        center: coordinate,
        zoom: 18,
        pitch: 60,
        essential: false,
        duration: 6000
      })
      const map_marker = new mapboxgl.Marker({ color: '#f23178' })
        .setLngLat(coordinate)
        .setPopup(popup)
        .addTo(map)
      doctor_markers.push(map_marker)
      setTimeout(() => {
        map_marker.togglePopup()
      }, 4700)
      continue
    }

    // Normal marker
    const map_marker = new mapboxgl.Marker({ color: '#5f66d9' })
      .setLngLat(coordinate)
      .setPopup(popup)
      .addTo(map)
    doctor_markers.push(map_marker)
  }
}
</script>

<template>
  <!-- 
    <client-only> ensures Mapbox (which references window/document) doesn't break SSR.
    If you're using Nuxt 3, the tag name is <ClientOnly>.
  -->
  <div class="sm:flex flex-row min-h-screen shadow-lg bg-primaryBg mb-4 px-4 pt-4 pb-20 mx-auto lg:px-8 lg:py-10 lg:mt-0">

    <!-- Map wrapper (relative), keep map container empty -->
    <div
      class="
        relative
        rounded-xl
        shadow-xl
        basis-2/3
        bg-slate-200
        m-1
        sm:min-h-screen
        min-h-[40vh]
        overflow-hidden
      "
      :class="{
        'hidden sm:flex': !showMapOnMobile,
        'block sm:flex': showMapOnMobile
      }"
    >
      <div id="doctor-map" class="w-full h-full"></div>
      <!-- Close map button (only visible on mobile if map is open) -->
      <button
        v-if="showMapOnMobile"
        class="absolute top-2 right-2 z-10 bg-white rounded-full p-2 text-gray-700 shadow-md hover:bg-gray-200 inline-flex items-center"
        @click="showMapOnMobile = false"
        aria-label="关闭地图"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5 mr-1"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          stroke-width="2"
        >
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
        关闭
      </button>
    </div>

    <!-- Right panel: search + list -->
    <div class="h-screen md:basis-1/3 flex flex-col">

     

      <!-- Search input + specialty filter -->
       
      <DoctorSearchInput class="mr-2 max-w-full" :v-show="!showMapOnMobile"
      :class="showMapOnMobile? 'mb-2': ''" />
      <DoctorSearchFilters class= "mt-2" v-show="!showMapOnMobile"
        v-model:specialty="filteringSpecialty"
      />

       <!-- Toggle map button (mobile only) -->
       <div class="block sm:hidden mb-3">
        <button
          class="ml-2 px-4 py-2 bg-indigo-500 -mb-4 text-white font-semibold rounded-md
                 hover:bg-indigo-600"
          @click="showMapOnMobile = !showMapOnMobile"
        >
          {{ showMapOnMobile ? '隐藏地图' : '显示地图' }}
        </button>
        <span class="text-gray-500 ml-5 text-md font-thin">{{ showMapOnMobile ? '轻触卡片可将地图飞至医生位置' : '' }}</span>
        
      </div>

      <!-- The listing + load more button -->
      <div class="flex-1 overflow-y-scroll mt-2 -mb-2" :class="{ '-mt-4': showMapOnMobile }">
        <DoctorList />
        <button
          class="text-white w-[calc(100%-16px)] ml-2 bg-secondaryBtn/90 
                 hover:bg-secondaryBtnHover font-medium rounded-lg text-sm
                 px-4 py-2 dark:bg-primary-600 dark:hover:bg-primary-700 
                 dark:focus:ring-primary-800"
          @click="loadMoreResults"
        >
          加载更多
        </button>
      </div>
    </div>
  </div>
</template>
